import {
  DayFormatEnum,
  HourFormatEnum,
  MonthFormatEnum,
  NotationOrderEnum,
  YearFormatEnum,
} from '@ulysses-inc/harami_api_client'
import { useCallback, useMemo } from 'react'
import {
  dayFormats,
  hourFormats,
  monthFormats,
  notationOrders,
  yearFormats,
} from './expiration'

export type PreservedAreaSettings = {
  expirationYear: YearFormatEnum
  expirationMonth: MonthFormatEnum
  expirationDay: DayFormatEnum
  expirationHour: HourFormatEnum
  expirationNotationOrder: NotationOrderEnum
}

const STORAGE_KEY = 'label-area-form-preserved-settings'

const defaultPreservedSettings: PreservedAreaSettings = {
  expirationYear: YearFormatEnum.YYYY,
  expirationMonth: MonthFormatEnum.MM,
  expirationDay: DayFormatEnum.DD,
  expirationHour: HourFormatEnum.NONE,
  expirationNotationOrder: NotationOrderEnum.YMDH,
}

const isValidYearFormat = (v: unknown): v is YearFormatEnum =>
  yearFormats.includes(v as YearFormatEnum)
const isValidMonthFormat = (v: unknown): v is MonthFormatEnum =>
  monthFormats.includes(v as MonthFormatEnum)
const isValidDayFormat = (v: unknown): v is DayFormatEnum =>
  dayFormats.includes(v as DayFormatEnum)
const isValidHourFormat = (v: unknown): v is HourFormatEnum =>
  hourFormats.includes(v as HourFormatEnum)
const isValidNotationOrder = (v: unknown): v is NotationOrderEnum =>
  notationOrders.includes(v as NotationOrderEnum)

const readFromStorage = (): PreservedAreaSettings => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return defaultPreservedSettings
    const parsed = JSON.parse(raw)

    return {
      expirationYear: isValidYearFormat(parsed.expirationYear)
        ? parsed.expirationYear
        : defaultPreservedSettings.expirationYear,
      expirationMonth: isValidMonthFormat(parsed.expirationMonth)
        ? parsed.expirationMonth
        : defaultPreservedSettings.expirationMonth,
      expirationDay: isValidDayFormat(parsed.expirationDay)
        ? parsed.expirationDay
        : defaultPreservedSettings.expirationDay,
      expirationHour: isValidHourFormat(parsed.expirationHour)
        ? parsed.expirationHour
        : defaultPreservedSettings.expirationHour,
      expirationNotationOrder: isValidNotationOrder(
        parsed.expirationNotationOrder,
      )
        ? parsed.expirationNotationOrder
        : defaultPreservedSettings.expirationNotationOrder,
    }
  } catch {
    return defaultPreservedSettings
  }
}

export const usePreservedAreaSettings = () => {
  const preservedSettings = useMemo(() => readFromStorage(), [])

  const updateSettings = useCallback(
    (settings: Partial<PreservedAreaSettings>) => {
      const current = readFromStorage()
      const updated = { ...current, ...settings }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
    },
    [],
  )

  return {
    preservedSettings,
    updateSettings,
  }
}
